import { useCallback, useEffect, useMemo, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import type { Me } from '../api/auth';
import { FLAG_STATUSES, familyModeration, unitModeration } from '../api/moderation';
import type { FlagRow, FlagStatus } from '../api/moderation';
import { AppHeader } from '../ui/AppHeader';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { EmptyState } from '../ui/EmptyState';
import { TextAreaField } from '../ui/Field';
import { Overlay } from '../ui/Overlay';
import { formatTimestamp } from '../lib/datetime';

/**
 * Reports — §5, TROOP-12. One screen for two readers, deliberately.
 *
 * **A reporter watches their own, and a reviewer works the queue.** They are the same rows read by
 * people with different standing, so this is one list and one route rather than two: a second
 * "your reports" screen would be a second answer to who may see a flag, which is the drift rule 13
 * exists to prevent. Every row says whether it was yours, and carries the server's own `canResolve`
 * — asked per row, because a reviewer does not resolve the report about their own message.
 *
 * **The value of a dismissed flag is on this screen.** §5's argument is that a flag produces an
 * attention event, a pattern record, and *proof somebody looked* — and the last one only exists if
 * the person who raised it can see the answer. So a closed report shows who closed it, when, and
 * what they wrote, to the reporter as much as to the queue.
 *
 * **The reader is never told a report about them exists.** That is the policy's doing and not this
 * screen's: rows about your own messages simply are not in the payload.
 *
 * **Both lists are bounded, and the open one is not the exception it looks like.** A unit with a
 * healthy backlog has two or three open reports, which is exactly the reasoning that leaves a list
 * unbounded and is exactly what the dev database disproves: smoke residue had this section at 20
 * rows and 7,348px within an afternoon, and it only ever grows. The true count stays in the
 * heading, so the bound is visible rather than a silent truncation — the same trade
 * `ParticipantStrip` makes for its eight chips and a `+N`.
 */
const OPEN_SHOWN = 8;
const CLOSED_SHOWN = 8;

export function ReportsScreen({
  unitId,
  unitName,
  me,
  onNavigate,
}: {
  /** Absent on the family door, which spans every unit this parent has standing in. */
  unitId?: string;
  unitName?: string;
  me: Me;
  onNavigate: (path: string) => void;
}) {
  const transport = useMemo(
    () => (unitId ? unitModeration(unitId) : familyModeration),
    [unitId],
  );

  const [rows, setRows] = useState<FlagRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState<FlagRow | null>(null);
  const [showAllOpen, setShowAllOpen] = useState(false);
  const [showAllClosed, setShowAllClosed] = useState(false);

  const load = useCallback(() => {
    transport
      .flags()
      .then(setRows)
      .catch(() => setError('Could not load reports.'));
  }, [transport]);

  useEffect(load, [load]);

  const open = (rows ?? []).filter((r) => r.status === 'Open');
  const closed = (rows ?? []).filter((r) => r.status !== 'Open');
  const openShown = showAllOpen ? open : open.slice(0, OPEN_SHOWN);
  const closedShown = showAllClosed ? closed : closed.slice(0, CLOSED_SHOWN);

  return (
    <Page>
      <AppHeader me={me} currentUnitId={unitId} onNavigate={onNavigate} />

      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            {unitName && (
              <p className="t-eyebrow" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
                {unitName}
              </p>
            )}
            <h1
              className="t-heading"
              style={{
                color: 'var(--color-text-primary)',
                margin: 'var(--space-2) 0 var(--space-2)',
              }}
            >
              Reports
            </h1>
            <p className="t-body" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
              Messages somebody raised a concern about. Whoever raised one can see what happened to
              it; the person it is about is not told.
            </p>
          </div>
        </Gutter>
      </section>

      <Gutter>
        <div
          className="flex flex-col"
          style={{ paddingBlock: 'var(--space-6)', gap: 'var(--space-7)' }}
        >
          {error && (
            <p className="t-body" role="alert" style={{ color: 'var(--color-status-blocked)', margin: 0 }}>
              {error}
            </p>
          )}

          {rows !== null && rows.length === 0 && (
            <EmptyState
              icon={ShieldCheck}
              headline="Nothing has been reported"
              explanation="Anyone in a thread can report a message in it, including young people. Reports land here."
            />
          )}

          {open.length > 0 && (
            <section>
              <h2
                className="t-eyebrow"
                style={{ color: 'var(--color-text-muted)', margin: '0 0 var(--space-2)' }}
              >
                Waiting for an answer &middot; <span className="tabular">{open.length}</span>
              </h2>
              {openShown.map((row) => (
                <FlagCard key={row.id} row={row} onResolve={() => setResolving(row)} />
              ))}

              {open.length > openShown.length && (
                <p style={{ margin: 'var(--space-3) 0 0' }}>
                  <button
                    type="button"
                    className="link-button t-caption"
                    onClick={() => setShowAllOpen(true)}
                  >
                    Show {open.length - openShown.length} more waiting
                  </button>
                </p>
              )}
            </section>
          )}

          {closed.length > 0 && (
            <section>
              <h2
                className="t-eyebrow"
                style={{ color: 'var(--color-text-muted)', margin: '0 0 var(--space-2)' }}
              >
                Answered &middot; <span className="tabular">{closed.length}</span>
              </h2>
              {closedShown.map((row) => (
                <FlagCard key={row.id} row={row} onResolve={() => setResolving(row)} />
              ))}

              {/* Bounded, and it says what it is hiding rather than truncating silently. */}
              {closed.length > closedShown.length && (
                <p style={{ margin: 'var(--space-3) 0 0' }}>
                  <button
                    type="button"
                    className="link-button t-caption"
                    onClick={() => setShowAllClosed(true)}
                  >
                    Show {closed.length - closedShown.length} more
                  </button>
                </p>
              )}
            </section>
          )}
        </div>
      </Gutter>

      {resolving && (
        <ResolveOverlay
          row={resolving}
          onClose={() => setResolving(null)}
          onResolved={() => {
            setResolving(null);
            load();
          }}
          resolve={transport.resolve}
        />
      )}

      <Footer />
    </Page>
  );
}

/**
 * One report. The flagged message's own words are here when the reader may see them — a reviewer
 * who cannot read what was said cannot review it, which is the whole job §5 asks of them.
 *
 * That sight is bounded to **this message**: it is not a way into the thread, its neighbours, or
 * its participant list, all of which stay `ConversationAccessPolicy`'s to decide (rule 13).
 */
function FlagCard({ row, onResolve }: { row: FlagRow; onResolve: () => void }) {
  return (
    <article
      style={{
        borderBottom: '1px solid var(--color-border-subtle)',
        paddingBlock: 'var(--space-4)',
      }}
    >
      <div className="flex items-baseline justify-between gap-3" style={{ flexWrap: 'wrap' }}>
        <p className="t-body-strong" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
          {reasonLabel(row.reason)}
        </p>
        <p className="t-caption tabular" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
          {formatTimestamp(row.raisedAt)}
        </p>
      </div>

      <p
        className="t-caption"
        style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-1) 0 0' }}
      >
        {row.raisedByMe ? 'You reported this' : `${row.raisedByDisplayName} reported this`} in{' '}
        {row.conversationTitle} · written by {row.messageAuthorDisplayName}
      </p>

      {row.note && (
        <p
          className="t-body"
          style={{ color: 'var(--color-text-primary)', margin: 'var(--space-3) 0 0' }}
        >
          “{row.note}”
        </p>
      )}

      <div
        style={{
          marginTop: 'var(--space-3)',
          border: '1px solid var(--color-border-subtle)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--space-3) var(--space-4)',
        }}
      >
        <p
          className="t-label"
          style={{ color: 'var(--color-text-muted)', margin: '0 0 var(--space-2)' }}
        >
          The message
        </p>
        <p
          className="t-body"
          style={{
            color: 'var(--color-text-primary)',
            margin: 0,
            overflowWrap: 'anywhere',
            fontStyle: row.messageBody === null ? 'italic' : undefined,
          }}
        >
          {row.messageBody ??
            (row.messageIsRemoved
              ? 'Taken down. Its words are kept, and are not yours to read.'
              : 'Not available.')}
        </p>
        {row.messageIsRemoved && row.messageBody !== null && (
          <p
            className="t-caption"
            style={{ color: 'var(--color-text-muted)', margin: 'var(--space-2) 0 0' }}
          >
            This message has since been taken down.
          </p>
        )}
      </div>

      {row.status === 'Open' ? (
        row.canResolve && (
          <div style={{ marginTop: 'var(--space-3)' }}>
            <Button onClick={onResolve}>Answer this</Button>
          </div>
        )
      ) : (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <p className="t-body-strong" style={{ color: 'var(--color-text-primary)', margin: 0 }}>
            {statusLabel(row.status)}
            {row.resolvedByDisplayName ? ` — ${row.resolvedByDisplayName}` : ''}
            {row.resolvedAt ? `, ${formatTimestamp(row.resolvedAt)}` : ''}
          </p>
          {row.resolutionNote && (
            <p
              className="t-body"
              style={{ color: 'var(--color-text-secondary)', margin: 'var(--space-1) 0 0' }}
            >
              {row.resolutionNote}
            </p>
          )}
        </div>
      )}
    </article>
  );
}

/**
 * Closing one.
 *
 * **A youth-protection report cannot be closed without a note**, and the note field says so rather
 * than the button being disabled with an explanation somewhere else. The server refuses it too —
 * `MessageFlag.Resolve` does, and so does a CHECK constraint — so this is the courtesy of saying it
 * before the reviewer loses their words, never the enforcement.
 */
function ResolveOverlay({
  row,
  onClose,
  onResolved,
  resolve,
}: {
  row: FlagRow;
  onClose: () => void;
  onResolved: () => void;
  resolve: (
    flagId: string,
    status: Exclude<FlagStatus, 'Open'>,
    note: string | null,
  ) => Promise<unknown>;
}) {
  const [status, setStatus] = useState<Exclude<FlagStatus, 'Open'>>('Reviewed');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const needsNote = row.reason === 'YptConcern';
  const missingNote = needsNote && note.trim().length === 0;

  return (
    <Overlay title="What came of this" onClose={onClose}>
      <div className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
        <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
          <legend className="t-label" style={{ color: 'var(--color-text-muted)' }}>
            What you did
          </legend>

          <div
            className="flex flex-col"
            style={{ gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}
          >
            {FLAG_STATUSES.map((option) => (
              <label
                key={option.value}
                className="flex gap-3"
                style={{
                  alignItems: 'flex-start',
                  minHeight: 44,
                  padding: 'var(--space-2)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="flag-status"
                  value={option.value}
                  checked={status === option.value}
                  onChange={() => setStatus(option.value)}
                  style={{ marginTop: 4 }}
                />
                <span>
                  <span className="t-body-strong" style={{ color: 'var(--color-text-primary)' }}>
                    {option.label}
                  </span>
                  <br />
                  <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
                    {option.hint}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <TextAreaField
          label="What you want on the record"
          hint={
            needsNote
              ? 'Required. This report concerns a young person’s safety, so it cannot be closed saying nothing.'
              : 'The person who raised this will read it.'
          }
          required={needsNote}
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        {error && (
          <p className="t-caption" role="alert" style={{ color: 'var(--color-status-blocked)', margin: 0 }}>
            {error}
          </p>
        )}

        <Button
          fullWidth
          disabledReason={
            missingNote
              ? 'A report about a young person’s safety needs a note saying what was done about it.'
              : busy
                ? 'Saving.'
                : undefined
          }
          onClick={() => {
            setBusy(true);
            setError(null);
            resolve(row.id, status, note.trim() || null)
              .then(onResolved)
              .catch((e: unknown) =>
                setError(e instanceof Error ? e.message : 'That did not save.'),
              )
              .finally(() => setBusy(false));
          }}
        >
          Close this report
        </Button>

        <Button variant="secondary" fullWidth onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Overlay>
  );
}

function reasonLabel(reason: FlagRow['reason']): string {
  switch (reason) {
    case 'YptConcern':
      return 'A young person’s safety';
    case 'Inappropriate':
      return 'Inappropriate';
    case 'OffTopic':
      return 'Off topic';
    default:
      return 'Something else';
  }
}

function statusLabel(status: FlagStatus): string {
  switch (status) {
    case 'Reviewed':
      return 'Looked at it';
    case 'Actioned':
      return 'Did something about it';
    case 'Escalated':
      return 'Passed it on';
    default:
      return 'Waiting';
  }
}
